import { uniq, uniqBy, find, isEmpty } from 'lodash';

export const getCourseCardCourseStatus = (course, userType = 'student') => {
  const {
    uuid,
    locked,
    taken,
    passed,
    errors,
    term,
    equivalentCourseTaken,
    addedInOtherRequirement,
    preselected,
    termsSelected,
  } = course;
  return {
    id: uuid,
    locked,
    taken,
    passed,
    errors,
    term,
    equivalentCourseTaken,
    addedInOtherRequirement,
    preselected,
    userType,
    ...(termsSelected?.[0]?.additional ?? {}),
  };
};

export const getCourseCardCourse = course => {
  const {
    courseId,
    courseInternalId,
    name,
    description,
    subjectName,
    credits,
    tags,
    gradesEligible,
    termsEligible,
    rawTermsEligible,
    prerequisitesMet,
    prerequisitesDescription,
    corequisitesMet,
    corequisitesDescription,
    termsSelectable,
    repeatable,
    stillRepeatable,
    length,
    allAcademicTerms,
    startingAcademicTerms,
    academicTermCount,
    prereq = {},
    coreq = {},
    termsSelected,
    grade,
  } = course;

  return {
    courseId,
    courseInternalId,
    name,
    description,
    credits,
    tags,
    termsSelectable,
    repeatable,
    stillRepeatable,
    length,
    allAcademicTerms,
    startingAcademicTerms,
    academicTermCount,
    grade,

    subject: { name: subjectName },
    gradesAvailable: gradesEligible || [],
    termsEligible: rawTermsEligible,
    academicTermsAvailable: termsEligible || [],
    prereq: prerequisitesDescription
      ? {
          met: prerequisitesMet,
          description: prerequisitesDescription,
        }
      : prereq,
    coreq: corequisitesDescription
      ? {
          met: corequisitesMet,
          description: corequisitesDescription,
        }
      : coreq,
    requirementNotes: undefined,
    ...(termsSelected?.[0]?.additional ?? {}),
  };
};

export const getCoursePickerCoursesAvailable = (rawCourses, isGuardian) =>
  rawCourses
    .filter(({ termsSelectable }) => termsSelectable.length > 0)
    .reduce(
      (
        partialCourses,
        {
          courseId,
          courseInternalId,
          name,
          uuid,
          showTerm,
          termsSelectable,
          termsEligible,
          tags,
          credits,
          subject,
          preemptErrors,
          description,
          bundleCourseId,
          bundleCourses,
          status,
          equivalentCourseTaken,
          addedInOtherRequirement,
          prereq,
          coreq,
          repeatable,
          stillRepeatable,
          length,
          allAcademicTerms,
          startingAcademicTerms,
          academicTermCount,
        },
      ) => [
        ...partialCourses,
        {
          courseId,
          courseInternalId,
          name,
          uuid,
          tags,
          credits,
          termsSelectable,
          subject,
          description,
          bundleCourseId,
          bundleCourses,
          equivalentCourseTaken,
          addedInOtherRequirement,
          prereq,
          coreq,
          repeatable,
          stillRepeatable,
          length,
          allAcademicTerms,
          academicTermCount,
          startingAcademicTerms,

          rawTermsEligible: termsEligible,
          gradesEligible: uniq(termsEligible.map(term => term.grade)),
          termsEligible: uniqBy(
            termsEligible.map(term => term.academicTerm),
            'academicTerm',
          ),
          taken: false,
          passed: false,
          errors: preemptErrors,
          locked:
            (((Array.isArray(termsSelectable) &&
              termsSelectable.length === 0) ||
              !Array.isArray(termsSelectable)) &&
              ['taken', 'failed'].includes(status)) ||
            !!equivalentCourseTaken ||
            isGuardian,
          subjectName: subject && subject.name,
          term: showTerm
            ? {
                name: `${termsSelectable
                  .map(({ academicTerm: { name: termName } }) => termName)
                  .join(', ')}`,
                shortName: termsSelectable
                  .map(({ academicTerm: { shortName } }) => shortName)
                  .join(' / '),
              }
            : undefined,
        },
      ],
      [],
    );

const getCourseInPickerStatus = ({ isFreePicker, gradeCourseStatus }) => {
  if (isFreePicker) {
    return ['taken', 'failed'].includes(gradeCourseStatus);
  }
  return false;
};

const getCourseInPickerLocked = ({
  isFreePicker,
  gradeCourseStatus,
  equivalentCourseTaken,
  repeatable,
  isGuardian,
  gradeConfiguration,
  grade,
}) => {
  if (isFreePicker) {
    return (
      gradeCourseStatus === 'taken' ||
      gradeCourseStatus === 'failed' ||
      (!!equivalentCourseTaken &&
        !repeatable &&
        gradeCourseStatus !== 'planned') ||
      isGuardian
    );
  }
  return (
    (gradeConfiguration[grade] && !gradeConfiguration[grade].editable) ||
    isGuardian
  );
};

const getCourseInPickerSelected = ({
  isFreePicker,
  gradeConfiguration,
  grade,
}) => {
  if (isFreePicker) {
    return true;
  }
  return gradeConfiguration[grade] && gradeConfiguration[grade].editable;
};

export const getCourseInPicker = ({
  rawCourses,
  isGuardian,
  isFreePicker = true,
  gradeConfiguration,
  sequenceName = 'main',
}) =>
  rawCourses
    .filter(({ termsSelected }) => termsSelected.length > 0)
    .reduce(
      (
        partialCourses,
        {
          courseId,
          courseInternalId,
          name,
          uuid,
          termsSelected,
          termsSelectable,
          termsEligible,
          tags,
          credits,
          showTerm,
          errors,
          description,
          bundleCourseId,
          bundleCourses,
          preselected,
          status,
          equivalentCourseTaken,
          enrolled,
          creditAwarded,
          addedInOtherRequirement,
          prereq,
          coreq,
          subject,
          repeatable,
          stillRepeatable,
          length,
          allAcademicTerms,
          startingAcademicTerms,
          academicTermCount,
          creditCount,
        },
      ) => [
        ...partialCourses,
        ...termsSelected.map(
          ({
            grade,
            academicTerm: { name: termName, shortName, academicTerm } = {},
          }) => {
            const matchTermsSelected = find(
              termsSelected,
              entry =>
                String(entry.grade) === String(grade) &&
                String(entry.academicTerm.academicTerm) ===
                  String(academicTerm),
            );
            const gradeCourseData = matchTermsSelected?.additional ?? {};
            const gradeCourseStatus = gradeCourseData?.status ?? status;
            const parsedStatus = getCourseInPickerStatus({
              isFreePicker,
              gradeCourseStatus,
            });
            const parsedLocked = getCourseInPickerLocked({
              isFreePicker,
              gradeCourseStatus,
              equivalentCourseTaken,
              repeatable,
              isGuardian,
              gradeConfiguration,
              grade,
            });
            const parsedSelected = getCourseInPickerSelected({
              isFreePicker,
              gradeConfiguration,
              grade,
            });
            return {
              courseId,
              courseInternalId,
              name,
              uuid,
              tags,
              rawTermsEligible: termsEligible,
              gradesEligible: uniq(termsEligible.map(term => term.grade)),
              termsEligible: uniqBy(
                termsEligible.map(term => term.academicTerm),
                'academicTerm',
              ),
              taken: parsedStatus,
              passed: status === 'taken',
              errors,
              locked: parsedLocked,
              selected: parsedSelected,
              credits,
              grade,
              term: showTerm
                ? {
                    name: termName,
                    shortName,
                  }
                : undefined,
              sequenceName,
              subjectName: subject && subject.name,
              description,
              bundleCourseId,
              bundleCourses,
              equivalentCourseTaken,
              addedInOtherRequirement,
              prereq,
              coreq,
              termsSelectable,
              repeatable,
              stillRepeatable,
              ...(gradeCourseData || {
                enrolled,
                creditAwarded,
                status,
                preselected,
                creditCount,
              }),
              length,
              allAcademicTerms,
              startingAcademicTerms,
              academicTermCount,
              termsSelected: matchTermsSelected ? [matchTermsSelected] : null,
            };
          },
        ),
      ],
      [],
    );

export const getCoursesInSequence = ({ sequences, activeSequenceUuid }) =>
  sequences.reduce(
    (partialCourses, sequence) => [
      ...partialCourses,
      ...sequence.courses
        .filter(course =>
          sequence.uuid === activeSequenceUuid
            ? course.termsSelected.length
            : course.termsSelectable.length,
        )
        .reduce(
          (
            partial,
            {
              courseId,
              courseInternalId,
              name,
              uuid,
              termsSelectable,
              termsSelected,
              termsEligible,
              tags,
              credits,
              description,
              equivalentCourseTaken,
              addedInOtherRequirement,
              prereq,
              coreq,
              subject,
              length,
              allAcademicTerms,
              startingAcademicTerms,
              academicTermCount,
              bundleCourseId,
              bundleCourses,
              status,
              preselected,
              repeatable,
              stillRepeatable,
            },
          ) => [
            ...partial,
            ...(sequence.uuid === activeSequenceUuid
              ? termsSelected
              : termsSelectable
            ).map(({ grade, academicTerm }) => {
              const matchTermsSelected = find(
                termsSelected,
                entry =>
                  String(entry.grade) === String(grade) &&
                  String(entry.academicTerm.academicTerm) ===
                    String(academicTerm),
              );
              return {
                courseId,
                courseInternalId,
                name,
                uuid,
                tags,
                credits,
                grade,
                description,
                equivalentCourseTaken,
                addedInOtherRequirement,
                prereq,
                coreq,
                subject,
                length,
                allAcademicTerms,
                startingAcademicTerms,
                academicTermCount,
                preselected,
                bundleCourseId,
                bundleCourses,
                termsSelectable,
                repeatable,
                stillRepeatable,

                rawTermsEligible: termsEligible,
                gradesEligible: uniq(termsEligible.map(term => term.grade)),
                termsEligible: uniqBy(
                  termsEligible.map(term => term.academicTerm),
                  'academicTerm',
                ),
                taken: ['taken', 'failed'].includes(status),
                passed: false,
                hasError: false,
                locked: true,
                noLockIcon: true,
                term: academicTerm,
                sequenceName: sequence.name,
                subjectName: subject && subject.name,
                termsSelected: matchTermsSelected ? [matchTermsSelected] : null,
              };
            }),
          ],
          [],
        ),
    ],
    [],
  );

const combineTerms = ({ key, courseInState, courseInSearch }) =>
  uniqBy(
    (courseInState?.[key] ?? []).concat(courseInSearch?.[key] ?? []),
    v => `${v.grade}-${v.academicTerm.academicTerm}`,
  );

export const combineSearchAndStateCourses = ({
  stateCourses,
  searchCourses,
}) => {
  const updatedStateCourses = stateCourses.map(courseInState => {
    const courseInSearch =
      searchCourses.filter(c => c.uuid === courseInState.uuid)?.[0] ?? {};
    if (!isEmpty(courseInSearch)) {
      return {
        ...courseInState,
        ...courseInSearch,
        termsSelectable: combineTerms({
          courseInState,
          courseInSearch,
          key: 'termsSelectable',
        }),
        termsSelected: combineTerms({
          courseInState,
          courseInSearch,
          key: 'termsSelected',
        }),
      };
    }
    return courseInState;
  });

  const updatedSearchCourses = searchCourses.map(courseInSearch => {
    const courseInState =
      stateCourses.filter(c => c.uuid === courseInSearch.uuid)?.[0] ?? {};
    if (!isEmpty(courseInState)) {
      return {
        ...courseInState,
        ...courseInSearch,
        termsSelectable: combineTerms({
          courseInSearch,
          courseInState,
          key: 'termsSelectable',
        }),
        termsSelected: combineTerms({
          courseInSearch,
          courseInState,
          key: 'termsSelected',
        }),
      };
    }
    return courseInSearch;
  });
  return uniqBy([...updatedStateCourses, ...updatedSearchCourses], 'uuid');
};
